import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { StructuredData } from '../components/SEO/StructuredData';

const products = [
  {
    id: 'fuel-ox-marine',
    name: 'FuelOX Marine',
    description: 'Fuel Ox Marine® with Combustion Catalyst additive is a unique, proprietary fuel treatment designed to improve marine vehicle performance and extend engine longevity. The enhanced stability and dispersant/detergent features make it indispensable in dealing with problem fuels or fuels in storage.',
    image: '/images/fuel-ox-marine.jpg',
    price: '49.99',
    category: 'Fuel Additives',
    benefits: [
      'Improves fuel efficiency',
      'Reduces emissions',
      'Prevents carbon buildup',
      'Extends engine life'
    ]
  },
  // Add more products here
];

export default function Products() {
  const siteUrl = 'https://marinefuelboost.com';
  const pageTitle = 'Marine Fuel Additives | Marine Fuel Boost';
  const pageDescription = 'Discover our premium range of marine fuel additives designed to enhance engine performance, reduce emissions, and protect your boat engine. Shop now for better fuel efficiency.';

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={`${siteUrl}/products`} />
        
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={`${siteUrl}/products`} />
        <meta property="og:type" content="product.group" />
      </Helmet>

      <StructuredData
        title={pageTitle}
        description={pageDescription}
        imageUrl={`${siteUrl}/images/fuel-dx-logo.png`}
        url={`${siteUrl}/products`}
      />

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-7xl mx-auto">
          <header className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">Marine Fuel Additives</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Enhance your boat's performance with our premium fuel additives. 
              Scientifically formulated to improve efficiency and protect your engine.
            </p>
          </header>

          <section className="mb-16">
            <h2 className="text-2xl font-semibold mb-6">Why Choose Our Fuel Additives?</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-3">Enhanced Performance</h3>
                <p>Improve fuel efficiency and engine power output with our advanced formulas.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-3">Engine Protection</h3>
                <p>Prevent carbon buildup and protect against corrosion in marine environments.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-3">Emission Reduction</h3>
                <p>Reduce harmful emissions and maintain environmental compliance.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-3">Cost Savings</h3>
                <p>Save on fuel costs and reduce engine maintenance expenses.</p>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-2xl font-semibold mb-6">Our Products</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {products.map(product => (
                <div key={product.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-64 object-cover"
                  />
                  <div className="p-6">
                    <h3 className="text-xl font-semibold mb-2">{product.name}</h3>
                    <p className="text-gray-600 mb-4">{product.description}</p>
                    <div className="mb-4">
                      <h4 className="font-medium mb-2">Key Benefits:</h4>
                      <ul className="list-disc pl-5">
                        {product.benefits.map((benefit, index) => (
                          <li key={index} className="text-gray-600">{benefit}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-bold">${product.price}</span>
                      <Link
                        to={`/products/${product.id}`}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
                      >
                        Learn More
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="bg-gray-50 p-8 rounded-lg">
            <h2 className="text-2xl font-semibold mb-6">Frequently Asked Questions</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-2">How do marine fuel additives work?</h3>
                <p className="text-gray-600">Our marine fuel additives work by cleaning fuel injectors, preventing carbon buildup, and improving fuel combustion efficiency.</p>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-2">Are your additives safe for all marine engines?</h3>
                <p className="text-gray-600">Yes, our additives are safe for all types of marine engines and meet strict safety and environmental standards.</p>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-2">How often should I use fuel additives?</h3>
                <p className="text-gray-600">We recommend using our additives with every tank fill for optimal performance and protection.</p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
} 